import type {
  AppNotificationRecipientType,
  AutomationDelayConfig,
  CreateWorkflowAutomationRequestModel,
  UpdateWorkflowAutomationRequestModel,
  WhatsAppDestinationType,
  WorkflowAutomationActionType,
  WorkflowAutomationTriggerType,
} from '../models/automation-api.models';
import { normalizeEmailRecipientType } from '../utils/email-action-config.utils';
import { normalizeAutomationCommentMessage } from '../utils/automation-comment-message.util';
import { workflowAutomationActionTypeToApiInt } from './automation-action-type.util';
import { workflowAutomationTriggerTypeToApiInt } from './automation-trigger-type.util';

/** Corpo enviado à API (POST/PUT) conforme contrato §1.1. */
export interface WorkflowAutomationWireBody {
  name: string;
  triggerType: number;
  triggerConfigJson: string;
  actionType: number;
  actionConfigJson: string;
  conditions: WireConditionBody[];
  actions: WireActionBody[];
  isActive: boolean;
  delayMode: number;
  delayValue: number;
  delayUnit: number;
  delayDateFieldId: string | null;
  delayRevalidateConditions: boolean;
}

export interface WireConditionBody {
  field: number;
  operator: number;
  value: string;
}

export interface WireActionBody {
  type: number;
  configJson: string;
}

const CONDITION_FIELD = { CardField: 1, CardStage: 2, ChecklistCompleted: 3, CardComments: 4 } as const;

/** Alinhado ao contrato API §3.3 — ConditionOperator. */
const CONDITION_OP: Record<string, number> = {
  Equals: 1,
  NotEquals: 2,
  GreaterThan: 3,
  LessThan: 4,
  Contains: 5,
  GreaterThanOrEqual: 6,
  LessThanOrEqual: 7,
  NotContains: 8,
  IsEmpty: 9,
  IsNotEmpty: 10,
  IsTrue: 11,
  IsFalse: 12,
  ChangedTo: 20,
  ChangedFromTo: 21,
  BecameFilled: 22,
  BecameEmpty: 23,
  DateIsToday: 30,
  DateIsOverdue: 31,
  DateIsWithinDays: 32,
} as const;

const DELAY_MODE: Record<string, number> = { Immediate: 0, RelativeToEvent: 1, RelativeToDateField: 2 };
const DELAY_UNIT: Record<string, number> = { Minutes: 1, Hours: 2, Days: 3 };

/** API §4.4 — string enum aceite no payload: FixedPhone, PersonField, PhoneField, CardAssignee, CardCreator. */
const WA_RECIPIENT_TO_API_STRING: Record<WhatsAppDestinationType, string> = {
  FixedNumber: 'FixedPhone',
  PersonField: 'PersonField',
  PhoneField: 'PhoneField',
  CardAssignee: 'CardAssignee',
  CardCreator: 'CardCreator',
};

const EMAIL_RECIPIENT_TO_INT: Record<string, number> = {
  FixedEmail: 1, PersonField: 2, EmailField: 3, CardAssignee: 4, CardCreator: 5,
};

function readString(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * §4.1: só CardFieldUpdated (3) admite objeto; demais exatamente "{}".
 */
export function serializeTriggerConfigJsonString(
  triggerType: WorkflowAutomationTriggerType,
  triggerConfigRecord: Record<string, unknown> | null | undefined
): string {
  if (triggerType === 'DailyAgenda') return JSON.stringify(triggerConfigRecord ?? {});
  if (triggerType !== 'CardFieldUpdated') {
    return '{}';
  }
  const cfg = triggerConfigRecord ?? null;
  const wf = readString(cfg?.['workflowFieldId']);
  const nk = readString(cfg?.['nativeCardFieldKey']);
  if (wf && nk) {
    return '{}';
  }
  if (nk) {
    return JSON.stringify({ nativeCardFieldKey: nk });
  }
  if (wf) {
    return JSON.stringify({ workflowFieldId: wf });
  }
  return '{}';
}

/**
 * CardMovedToStage: etapa vai em condição CardStage + Equals (§4).
 */
export function buildWireConditionsFromPayload(
  triggerType: WorkflowAutomationTriggerType,
  triggerConfigRecord: Record<string, unknown> | null | undefined
): WireConditionBody[] {
  if (triggerType !== 'CardMovedToStage') return [];
  const stageId = readString(triggerConfigRecord?.['targetStageId']);
  if (!stageId) return [];
  return [
    {
      field: CONDITION_FIELD.CardStage,
      operator: CONDITION_OP['Equals'],
      value: stageId,
    },
  ];
}

function destinationToRecipientApiString(d: WhatsAppDestinationType): string {
  return WA_RECIPIENT_TO_API_STRING[d] ?? 'FixedPhone';
}

function buildWhatsAppWireObject(config: Record<string, unknown>): Record<string, unknown> {
  const templateKey = readString(config['templateKey']);
  const variables =
    config['variables'] && typeof config['variables'] === 'object' && !Array.isArray(config['variables'])
      ? (config['variables'] as Record<string, unknown>)
      : {};
  const varsOut: Record<string, string> = {};
  for (const [k, v] of Object.entries(variables)) {
    varsOut[k] = typeof v === 'string' ? v : String(v ?? '');
  }
  const destRaw = readString(config['destinationType']) || readString(config['recipientType']);
  const legacyPhone = readString(config['targetPhone']);
  let dest: WhatsAppDestinationType = 'FixedNumber';
  if (
    destRaw === 'FixedNumber'
    || destRaw === 'FixedPhone'
    || (!destRaw && legacyPhone)
  ) {
    dest = 'FixedNumber';
  } else if (
    destRaw === 'PersonField'
    || destRaw === 'CardAssignee'
    || destRaw === 'PhoneField'
    || destRaw === 'CardCreator'
  ) {
    dest = destRaw as WhatsAppDestinationType;
  }
  const out: Record<string, unknown> = {
    templateKey,
    variables: varsOut,
    recipientType: destinationToRecipientApiString(dest),
  };
  if (dest === 'FixedNumber') {
    out['targetPhone'] = readString(config['targetPhone']);
  }
  const rwf =
    readString(config['recipientWorkflowFieldId'])
    || (dest === 'PersonField' ? readString(config['personWorkflowFieldId']) : '')
    || (dest === 'PhoneField' ? readString(config['phoneWorkflowFieldId']) : '');
  if (dest === 'PersonField' || dest === 'PhoneField') {
    if (rwf) out['recipientWorkflowFieldId'] = rwf;
  }
  return out;
}

function buildSendAppNotificationWireObject(config: Record<string, unknown>): Record<string, unknown> {
  const recipientType = readString(config['recipientType']);
  const out: Record<string, unknown> = {
    recipientType,
    title: readString(config['title']),
    message: readString(config['message']),
  };
  if (recipientType === 'SpecificUser') {
    out['userId'] = readString(config['userId']);
  }
  if (recipientType === 'PersonField') {
    out['recipientWorkflowFieldId'] = readString(config['recipientWorkflowFieldId']);
  }

  const exp = config['expiration'] as Record<string, unknown> | null | undefined;
  if (exp && typeof exp === 'object' && exp['enabled'] === true) {
    const quantity = typeof exp['amount'] === 'number' ? exp['amount'] : 1;
    const unitStr = readString(exp['unit']) || 'Hours';
    out['expiration'] = { quantity, unit: unitStr };
  }

  return out;
}

function buildSendEmailTemplateWireObject(config: Record<string, unknown>): Record<string, unknown> {
  const templateKey = readString(config['templateKey']);
  const dest = normalizeEmailRecipientType(config['recipientType'] ?? config['destinationType']);
  const out: Record<string, unknown> = {
    templateId: templateKey,
    recipientType: EMAIL_RECIPIENT_TO_INT[dest] ?? 1,
  };
  if (dest === 'FixedEmail') {
    out['targetEmail'] = readString(config['targetEmail']);
  }
  if (dest === 'PersonField' || dest === 'EmailField') {
    const wf = readString(config['recipientWorkflowFieldId']);
    if (wf) out['recipientFieldId'] = wf;
  }
  return out;
}

export function serializeActionConfigJson(
  actionType: WorkflowAutomationActionType,
  actionConfigRecord: Record<string, unknown> | null | undefined
): string {
  const c = actionConfigRecord ?? {};
  if (actionType === 'MoveCardToStage') {
    const id = readString(c['targetStageId']);
    return JSON.stringify(id ? { targetStageId: id } : {});
  }
  if (actionType === 'CreateCardComment') {
    const message = normalizeAutomationCommentMessage(readString(c['message'] ?? c['commentText']));
    return JSON.stringify(message ? { message } : {});
  }
  if (actionType === 'AssignUserToCard') {
    const userId = readString(c['userId']);
    return JSON.stringify(userId ? { userId } : {});
  }
  if (actionType === 'SendWhatsAppTemplate') {
    return JSON.stringify(buildWhatsAppWireObject(c));
  }
  if (actionType === 'SendEmailTemplate') {
    return JSON.stringify(buildSendEmailTemplateWireObject(c));
  }
  if (actionType === 'SendAppNotification') {
    return JSON.stringify(buildSendAppNotificationWireObject(c));
  }
  if (actionType === 'SendWhatsAppSessionMessage') {
    return JSON.stringify({ messageTemplate: readString(c['messageTemplate']) });
  }
  if (actionType === 'UpdateCardField') {
    const wfId = readString(c['workflowFieldId']);
    // Destino nativo (título do card) não tem workflowFieldId — viaja como nativeCardFieldKey.
    const nativeKey = readString(c['nativeCardFieldKey']);
    if (!wfId && !nativeKey) return '{}';
    return JSON.stringify(c);
  }
  if (actionType === 'SetCardDueDate') {
    // Modo relativo: { mode: "relative", value: N, unit: "Minutes"|"Hours"|"Days" }
    const mode = typeof c['mode'] === 'string' ? c['mode'] : '';
    if (mode === 'relative') {
      return JSON.stringify({
        mode: 'relative',
        value: typeof c['value'] === 'number' ? c['value'] : 1,
        unit: typeof c['unit'] === 'string' ? c['unit'] : 'Hours',
      });
    }
    // Modo fixo (legado): { dueDate: "..." }
    const due = c['dueDate'] ?? c['dueDateValue'];
    if (due === null || due === undefined) {
      return JSON.stringify({ dueDate: null });
    }
    if (typeof due === 'string') {
      const t = due.trim();
      return JSON.stringify({ dueDate: t.length ? t : null });
    }
    return JSON.stringify({ dueDate: null });
  }
  return '{}';
}

function serializeDelayFields(cfg: AutomationDelayConfig | null | undefined): {
  delayMode: number;
  delayValue: number;
  delayUnit: number;
  delayDateFieldId: string | null;
  delayRevalidateConditions: boolean;
} {
  if (!cfg || cfg.mode === 'Immediate') {
    return { delayMode: 0, delayValue: 0, delayUnit: 1, delayDateFieldId: null, delayRevalidateConditions: true };
  }
  let delayValue = cfg.delayAmount;
  if (cfg.mode === 'RelativeToDateField' && cfg.dateRelation === 'Before') {
    delayValue = -Math.abs(delayValue);
  }
  if (cfg.mode === 'RelativeToDateField' && cfg.dateRelation === 'OnDay') {
    delayValue = 0;
  }
  return {
    delayMode: DELAY_MODE[cfg.mode] ?? 0,
    delayValue,
    delayUnit: DELAY_UNIT[cfg.delayUnit] ?? 1,
    delayDateFieldId: cfg.mode === 'RelativeToDateField' ? (cfg.dateFieldId || null) : null,
    delayRevalidateConditions: cfg.revalidateConditions ?? true,
  };
}

export function toWireAutomationBody(
  payload: CreateWorkflowAutomationRequestModel | UpdateWorkflowAutomationRequestModel
): WorkflowAutomationWireBody {
  const triggerCfg = payload.triggerConfigJson ?? null;
  const actionCfg = payload.actionConfigJson ?? null;
  const triggerConditions = buildWireConditionsFromPayload(payload.triggerType, triggerCfg);

  const extraConditions: WireConditionBody[] = (payload.conditions ?? []).map(c => ({
    field: CONDITION_FIELD[c.field] ?? 1,
    operator: CONDITION_OP[c.operator] ?? 1,
    value: typeof c.value === 'string' ? c.value : JSON.stringify(c.value ?? ''),
  }));

  const conditions = [...triggerConditions, ...extraConditions];
  const triggerJson = serializeTriggerConfigJsonString(payload.triggerType, triggerCfg);
  const actionTypeInt = workflowAutomationActionTypeToApiInt(payload.actionType);
  const configJson = serializeActionConfigJson(payload.actionType, actionCfg);
  const delay = serializeDelayFields(payload.delayConfig);

  return {
    name: payload.name.trim(),
    triggerType: workflowAutomationTriggerTypeToApiInt(payload.triggerType),
    triggerConfigJson: triggerJson,
    actionType: actionTypeInt,
    actionConfigJson: configJson,
    conditions,
    actions: [{ type: actionTypeInt, configJson }],
    isActive: payload.isActive,
    ...delay,
  };
}

/** Converte JSON de ação da API para o formato interno (ex.: WhatsApp). */
export function normalizeActionConfigFromApi(
  actionType: WorkflowAutomationActionType,
  rawConfig: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!rawConfig || typeof rawConfig !== 'object') return null;
  if (actionType === 'CreateCardComment') {
    const text = normalizeAutomationCommentMessage(readString(rawConfig['message'] ?? rawConfig['commentText']));
    return { message: text };
  }
  if (actionType === 'SetCardDueDate') {
    const mode = typeof rawConfig['mode'] === 'string' ? rawConfig['mode'] : '';
    if (mode === 'relative') {
      return {
        mode: 'relative',
        value: typeof rawConfig['value'] === 'number' ? rawConfig['value'] : 1,
        unit: typeof rawConfig['unit'] === 'string' ? rawConfig['unit'] : 'Hours',
      };
    }
    const val = rawConfig['dueDateValue'] ?? rawConfig['dueDate'];
    return { dueDate: val ?? null };
  }
  if (actionType === 'SendEmailTemplate') {
    const templateKey = readString(rawConfig['templateId'] ?? rawConfig['templateKey']);
    const recipientRaw = rawConfig['recipientType'] ?? rawConfig['destinationType'];
    const dest = typeof recipientRaw === 'number'
      ? emailRecipientFromInt(recipientRaw)
      : normalizeEmailRecipientType(recipientRaw);
    const out: Record<string, unknown> = {
      templateKey,
      recipientType: dest,
    };
    if (dest === 'FixedEmail') {
      out['targetEmail'] = readString(rawConfig['targetEmail']);
    }
    if (dest === 'PersonField' || dest === 'EmailField') {
      out['recipientWorkflowFieldId'] = readString(
        rawConfig['recipientFieldId'] ?? rawConfig['recipientWorkflowFieldId']
      );
    }
    return out;
  }
  if (actionType === 'SendAppNotification') {
    const expRaw = rawConfig['expiration'] as Record<string, unknown> | null | undefined;
    let expiration: Record<string, unknown> | null = null;
    if (expRaw && typeof expRaw === 'object') {
      const EXPIRATION_UNIT_FROM_INT: Record<number, string> = { 1: 'Minutes', 2: 'Hours', 3: 'Days' };
      const rawUnit = expRaw['unit'];
      const unit = typeof rawUnit === 'number'
        ? (EXPIRATION_UNIT_FROM_INT[rawUnit] ?? 'Hours')
        : (readString(rawUnit) || 'Hours');
      const quantity = typeof expRaw['quantity'] === 'number' ? expRaw['quantity']
        : (typeof expRaw['amount'] === 'number' ? expRaw['amount'] : 1);
      expiration = {
        enabled: true,
        amount: quantity,
        unit,
      };
    }
    return {
      recipientType: readString(rawConfig['recipientType']),
      userId: readString(rawConfig['userId']),
      recipientWorkflowFieldId: readString(rawConfig['recipientWorkflowFieldId']),
      title: readString(rawConfig['title']),
      message: readString(rawConfig['message']),
      expiration,
    };
  }
  if (actionType === 'SendWhatsAppSessionMessage') {
    return { messageTemplate: readString(rawConfig['messageTemplate']) };
  }
  if (actionType === 'SendWhatsAppTemplate') {
    const templateKey = readString(rawConfig['templateName'] ?? rawConfig['templateKey']);
    const recipientRaw = rawConfig['recipientType'];
    let destinationType: WhatsAppDestinationType = 'FixedNumber';
    if (typeof recipientRaw === 'number') {
      destinationType = waRecipientFromInt(recipientRaw);
    } else {
      const s = readString(recipientRaw);
      if (s === 'FixedPhone' || s === 'FixedNumber') destinationType = 'FixedNumber';
      else if (s === 'PersonField' || s === 'CardAssignee' || s === 'PhoneField' || s === 'CardCreator') {
        destinationType = s as WhatsAppDestinationType;
      }
    }
    const rwf = readString(rawConfig['recipientWorkflowFieldId']);
    const out: Record<string, unknown> = {
      templateKey,
      destinationType,
      variables: rawConfig['variables'] && typeof rawConfig['variables'] === 'object'
        ? rawConfig['variables']
        : {},
    };
    if (destinationType === 'FixedNumber') {
      out['targetPhone'] = readString(rawConfig['targetPhone']);
    }
    if (destinationType === 'PersonField') {
      out['personWorkflowFieldId'] = rwf || readString(rawConfig['personWorkflowFieldId']);
    }
    if (destinationType === 'PhoneField') {
      out['phoneWorkflowFieldId'] = rwf || readString(rawConfig['phoneWorkflowFieldId']);
    }
    return out;
  }
  return rawConfig;
}

function waRecipientFromInt(n: number): WhatsAppDestinationType {
  const map: Record<number, WhatsAppDestinationType> = {
    1: 'FixedNumber', 2: 'PersonField', 3: 'CardAssignee', 4: 'PhoneField', 5: 'CardCreator',
  };
  return map[n] ?? 'FixedNumber';
}

function emailRecipientFromInt(n: number): string {
  const map: Record<number, string> = {
    1: 'FixedEmail', 2: 'PersonField', 3: 'EmailField', 4: 'CardAssignee', 5: 'CardCreator',
  };
  return map[n] ?? 'FixedEmail';
}

/**
 * Se o gatilho é CardMovedToStage e há condição CardStage Equals, expõe targetStageId no record de UI.
 */
function isWireCardStageField(field: unknown): boolean {
  if (field === CONDITION_FIELD.CardStage) return true;
  if (field === '2' || field === 2) return true;
  const s = typeof field === 'string' ? field.toLowerCase().replace(/_/g, '') : '';
  return s === 'cardstage';
}

function isWireEqualsOp(operator: unknown): boolean {
  if (operator === CONDITION_OP['Equals']) return true;
  if (operator === '1' || operator === 1) return true;
  const s = typeof operator === 'string' ? operator.toLowerCase().replace(/_/g, '') : '';
  return s === 'equals';
}

export function mergeTriggerConfigFromWireConditions(
  triggerType: WorkflowAutomationTriggerType,
  triggerConfigParsed: Record<string, unknown> | null,
  conditions: unknown[]
): Record<string, unknown> | null {
  if (triggerType !== 'CardMovedToStage') return triggerConfigParsed;
  for (const c of conditions) {
    if (!c || typeof c !== 'object') continue;
    const rec = c as Record<string, unknown>;
    const field = rec['field'];
    const operator = rec['operator'];
    const value = rec['value'];
    if (isWireCardStageField(field) && isWireEqualsOp(operator)) {
      const stageId = typeof value === 'string' ? value.trim() : String(value ?? '').trim();
      if (stageId) {
        return { ...(triggerConfigParsed ?? {}), targetStageId: stageId };
      }
    }
  }
  return triggerConfigParsed;
}

/** Reidrata triggerConfig a partir do JSON de gatilho + condições embutidas na resposta. */
export function parseTriggerConfigForUi(
  triggerType: WorkflowAutomationTriggerType,
  triggerConfigJsonParsed: Record<string, unknown> | null,
  conditions: unknown[]
): Record<string, unknown> | null {
  if (triggerType === 'CardFieldUpdated') {
    return triggerConfigJsonParsed;
  }
  if (triggerType === 'CardMovedToStage') {
    return mergeTriggerConfigFromWireConditions(triggerType, triggerConfigJsonParsed, conditions);
  }
  return triggerConfigJsonParsed;
}

/** Resolve delay mode de int, string numérica ou string enum. */
function resolveDelayModeInt(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
    const DELAY_MODE_FROM_STRING: Record<string, number> = {
      immediate: 0, relativetoevent: 1, relativetodatefield: 2,
    };
    return DELAY_MODE_FROM_STRING[raw.toLowerCase().replace(/[_\s]/g, '')] ?? 0;
  }
  return 0;
}

/** Resolve delay unit de int, string numérica ou string enum. */
function resolveDelayUnitInt(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
    const DELAY_UNIT_FROM_STRING: Record<string, number> = {
      minutes: 1, hours: 2, days: 3,
    };
    return DELAY_UNIT_FROM_STRING[raw.toLowerCase().replace(/[_\s]/g, '')] ?? 1;
  }
  return 1;
}

/** Converte campos flat de delay da API para AutomationDelayConfig. */
export function parseDelayConfigFromApi(source: Record<string, unknown>): AutomationDelayConfig | null {
  const modeRaw = source['delayMode'] ?? source['DelayMode'];
  const mode = resolveDelayModeInt(modeRaw);
  if (mode === 0) return null;

  const DELAY_MODE_FROM_INT: Record<number, AutomationDelayConfig['mode']> = {
    1: 'RelativeToEvent', 2: 'RelativeToDateField',
  };
  const DELAY_UNIT_FROM_INT: Record<number, AutomationDelayConfig['delayUnit']> = {
    1: 'Minutes', 2: 'Hours', 3: 'Days',
  };

  const delayValueRaw = source['delayValue'] ?? source['DelayValue'];
  const delayValue = typeof delayValueRaw === 'number' ? delayValueRaw
    : typeof delayValueRaw === 'string' && Number.isFinite(Number(delayValueRaw)) ? Number(delayValueRaw)
    : 0;
  const unitRaw = resolveDelayUnitInt(source['delayUnit'] ?? source['DelayUnit']);

  let dateRelation: 'Before' | 'OnDay' | 'After' = 'After';
  if (mode === 2) {
    if (delayValue < 0) dateRelation = 'Before';
    else if (delayValue === 0) dateRelation = 'OnDay';
    else dateRelation = 'After';
  }

  const dateFieldId = source['delayDateFieldId'] ?? source['DelayDateFieldId'];
  const revalidate = source['delayRevalidateConditions'] ?? source['DelayRevalidateConditions'];

  return {
    mode: DELAY_MODE_FROM_INT[mode] ?? 'RelativeToEvent',
    delayAmount: Math.abs(delayValue),
    delayUnit: DELAY_UNIT_FROM_INT[unitRaw] ?? 'Minutes',
    dateFieldId: typeof dateFieldId === 'string' ? dateFieldId : null,
    dateRelation,
    revalidateConditions: revalidate !== false,
  };
}
